<template>
  <div v-loading="loading" class="quiz">
    <ul v-if="questions.length" class="quiz__progress">
      <span v-for="(_, index) in questions" :key="index">
        <li
          :class="[
            'quiz__progress-item',
            {
              'quiz__progress-item--active': index === selectedQuestionIndex || index <= selectedQuestionIndex,
            },
          ]"
        >
          {{ index + 1 }}
        </li>
      </span>
    </ul>
    <div class="d-flex">
      <div class="quiz__container">
        <div v-if="questions.length" class="question">
          <div class="question__info">
            <h1 class="question__info-text text-uppercase">
              {{ $t('testTest.doubleClickCorrect') }}
            </h1>
            <transition name="fade">
              <span v-if="countDownTimerStatus" class="question__info-time">{{ timePerQuestion | secondsToTime }}</span>
            </transition>
          </div>
          <div class="py-2 px-1">
            <template v-if="renderQuestion">
              <h3 class="question__text">{{ questions[selectedQuestionIndex].text }}</h3>
              <h2 class="question__example">{{ questions[selectedQuestionIndex].example }}</h2>
            </template>
            <template v-if="questions[selectedQuestionIndex].type === 'M'">
              <ul class="question__multiple-choices">
                <template v-for="option in questions[selectedQuestionIndex].options">
                  <li v-if="option" :key="option" class="question__multiple-choices-item">
                    <button
                      :class="[
                        'btn',
                        'question__multiple-choices-item-button',
                        { 'question__multiple-choices-item-button--active': userAnswer === option },
                      ]"
                      :disabled="userAnswerIsCorrect"
                      @click="userAnswer = option"
                    >
                      {{ option }}
                    </button>
                  </li>
                </template>
              </ul>
            </template>
            <template v-if="questions[selectedQuestionIndex].type === 'B'">
              <ul class="question__two-choices">
                <template v-for="option in questions[selectedQuestionIndex].options">
                  <li v-if="option" :key="option" class="question__two-choices-item">
                    <button
                      :class="[
                        'btn',
                        'question__two-choices-item-button',
                        { 'question__two-choices-item-button--active': userAnswer === option },
                      ]"
                      :disabled="userAnswerIsCorrect"
                      @click="userAnswer = option"
                    >
                      {{ option }}
                    </button>
                  </li>
                </template>
              </ul>
            </template>
            <template v-if="questions[selectedQuestionIndex].type === 'T'">
              <div class="question__write-answer">
                <input
                  v-model="userAnswer"
                  type="text"
                  :disabled="userAnswerIsCorrect"
                  class="question__write-answer-input form-control"
                />
              </div>
            </template>
          </div>
          <div class="question__actions">
            <button v-if="userAnswerIsCorrect" class="btn btn-skip" :disabled="!userAnswer" @click.once="nextQuestion">
              {{ $t('pretest.next') }}
            </button>
            <button
              v-if="(!questionTypeT && !userAnswerIsCorrect) || (questionTypeT && !userAnswerIsCorrect && !timeOver)"
              class="btn btn-submit"
              :disabled="!userAnswer"
              @click="handleUserAnswer"
            >
              {{ $t('pretest.submit') }}
            </button>
            <button
              v-if="!userAnswerIsCorrect && timeOver && questionTypeT"
              class="btn btn-skip"
              :disabled="!userAnswer"
              @click.once="nextQuestion"
            >
              {{ $t('pretest.next') }}
            </button>
          </div>
        </div>
        <div v-else class="d-flex justify-content-center align-items-center flex-column h-100">
          <h1>This lesson has no questions</h1>
          <button
            type="button"
            class="btn btn-info"
            @click="
              $router.push({
                name: 'subject',
                params: {
                  grade_id: $route.params.grade_id,
                  subject_id: $route.params.subject_id,
                },
                query: {
                  cluster_id: $route.params.cluster_id,
                },
              })
            "
          >
            Go back
          </button>
        </div>
      </div>
      <div :class="['quiz__callback', { 'quiz__callback--display': displayCallback }]">
        <span ref="callbackText" class="quiz__callback-text">{{ callbackText }}</span>
      </div>
    </div>
    <button
      type="button"
      class="btn btn-exit"
      @click="
        $router.push({
          name: 'subject',
          params: {
            grade_id: $route.params.grade_id,
            subject_id: $route.params.subject_id,
          },
          query: {
            cluster_id: $route.params.cluster_id,
          },
        })
      "
    >
      <img src="@/assets/img/close.png" alt />
      {{ $t('pretest.exit') }}
    </button>

    <el-dialog title="Results" :visible.sync="dialogVisible" width="90%" :before-close="handleClose">
      <Results :quiz="quiz" />
    </el-dialog>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import audioCorrect1 from '@/assets/audio/correct1.mp3';
import audioCorrect2 from '@/assets/audio/correct2.mp3';
import audioCorrect3 from '@/assets/audio/correct3.mp3';
import audioCorrect4 from '@/assets/audio/correct4.mp3';
import audioIncorrect1 from '@/assets/audio/incorrect1.mp3';
import audioIncorrect2 from '@/assets/audio/incorrect2.mp3';
import audioIncorrect3 from '@/assets/audio/incorrect3.mp3';
import audioIncorrect4 from '@/assets/audio/incorrect4.mp3';
import audioIncorrect5 from '@/assets/audio/incorrect5.mp3';
import audioIncorrect6 from '@/assets/audio/incorrect6.mp3';
import Results from './results/index.vue';

export default {
  name: 'Quiz',
  components: {
    Results,
  },
  data() {
    return {
      countDownTimerStatus: true,
      renderQuestion: true,
      timeoutId: null,
      questions: [],
      dialogVisible: false,
      selectedQuestionIndex: 0,
      timePerQuestion: 180,
      loading: false,
      userAnswer: null,
      secondsPerQuestion: 180,
      userAnswerIsCorrect: false,
      userAnswerReviewed: false,
      questionTypeT: false,
      timeOver: false,
      quiz: {
        questionsCorrect: 0,
        wrongQuestions: 0,
        average: 0,
        startedDate: null,
        finishDate: null,
      },
      callbackText: '',
      displayCallback: false,
      setTimeout: null,
      audio: null,
      audios: {
        correct: [audioCorrect1, audioCorrect2, audioCorrect3, audioCorrect4],
        incorrect: [
          audioIncorrect1,
          audioIncorrect2,
          audioIncorrect3,
          audioIncorrect4,
          audioIncorrect5,
          audioIncorrect6,
        ],
      },
    };
  },
  computed: {
    ...mapGetters({
      authenticatedUser: 'auth/authenticatedUser',
      currentLesson: 'lesson/currentLesson',
      subject: 'subject/currentSubject',
    }),
  },
  watch: {
    subject: function (newValue) {
      console.log(newValue);
    },
  },
  created() {
    this.fetchQuestions();
    window.onbeforeunload = function (event) {
      console.log(event);
      return confirm('Confirm refresh');
    };
    // set time per question
  },
  mounted() {
    // this.timePerQuestion = this.subject.seconds_per_question ? this.subject.seconds_per_question : 120000;
    // console.log(this.timePerQuestion);
    // console.log(this.subject.seconds_per_question);
  },
  destroyed() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    countDownTimer() {
      if (this.timePerQuestion > 0) {
        this.timeOver = false;
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.timePerQuestion -= 1;
          this.countDownTimer();
          this.countDownTimerStatus = false;
          setTimeout(() => {
            this.countDownTimerStatus = true;
          }, 100);
        }, 1000);
      } else {
        this.timeOver = true;
        this.handleUserAnswer();
      }
    },
    handleClose() {
      this.dialogVisible = false;
    },
    fetchQuestions() {
      this.loading = true;
      this.$httpMain
        .post(`lesson/questions`, {
          lesson_id: Number.parseInt(this.$route.params.lesson_id),
          subject_id: Number.parseInt(this.$route.params.subject_id),
          user_id: Number.parseInt(this.authenticatedUser.id),
        })
        .then(res => {
          this.questions = res.data.questions;
          this.secondsPerQuestion = res.data.seconds_per_question ? res.data.seconds_per_question : 45;
          this.timePerQuestion = this.secondsPerQuestion;
          this.countDownTimer();
          console.log(this.timePerQuestion);
        })
        .catch(() => {
          this.questions = [];
          this.quizFinished();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    nextQuestion() {
      // let question = this.questions[this.selectedQuestionIndex];

      if (this.selectedQuestionIndex < this.questions.length - 1) {
        this.renderQuestion = false;
        this.selectedQuestionIndex++;
        this.userAnswerIsCorrect = false;
        this.userAnswerReviewed = false;
        this.userAnswer = null;
        this.questionTypeT = false;
        this.timePerQuestion = this.secondsPerQuestion ? this.secondsPerQuestion : 180;
        this.countDownTimer();
        setTimeout(() => {
          this.renderQuestion = true;
        }, 100);
        return;
      }

      this.quiz.average = (100 / this.questions.length) * this.quiz.questionsCorrect;
      this.dialogVisible = true;
      this.updateTraking();
      if (this.quiz.average >= 75) {
        this.quizFinished();
        this.$store.state.subject.currentSubject.completed_lessons++;
      }
    },
    quizFinished() {
      this.$httpMain
        .post(`save_lesson_progress`, {
          lesson_id: this.currentLesson.id,
          user_id: this.authenticatedUser.id,
          state_id: 3,
        })
        .then(res => {
          console.log(res.data);
        });
    },
    updateTraking() {
      this.$httpMain.put('lesson-tracking', {
        id: Number.parseInt(this.$route.query.t),
        quiz_presented: true,
        quiz_score_latest: this.quiz.average,
        lesson_id: this.$route.params.lesson_id,
        user_id: this.authenticatedUser.id,
      });
    },
    handleUserAnswer() {
      var answerCorrect = this.questions[this.selectedQuestionIndex].answer_correct;
      this.questionTypeT = false;

      if (this.questions[this.selectedQuestionIndex].type === 'T') {
        this.questionTypeT = true;
        answerCorrect = this.questions[this.selectedQuestionIndex].answer_correct.toLowerCase();
        this.userAnswer = this.userAnswer != null ? this.userAnswer.toLowerCase() : this.userAnswer;
      }
      if (this.userAnswer === answerCorrect) {
        if (!this.userAnswerReviewed) {
          this.quiz.questionsCorrect++;
          this.userAnswerReviewed = true;
        }
        this.audio = new Audio(this.audios.correct[this.selectedQuestionIndex]);
        this.audio.play();
        this.callbackText = this.$t('pretest.correctAnswer');
        this.$refs.callbackText.style.color = '#35aa4e';
        this.displayCallback = true;
        this.userAnswerIsCorrect = true;
      } else {
        this.callbackText = this.$t('pretest.tryAgain');
        this.$refs.callbackText.style.color = '#FF1800';

        if (!this.questionTypeT || (this.questionTypeT && this.timeOver)) {
          if (!this.userAnswerReviewed) {
            this.quiz.wrongQuestions++; //
            this.userAnswerReviewed = true;
          }
        }

        this.audio = new Audio(this.audios.incorrect[Math.floor(Math.random() * 6)]);
        this.audio.play();
        this.displayCallback = true;
      }
      // if (this.questionTypeT && this.userAnswer === null) {
      //   this.quiz.wrongQuestions++;
      // }
      clearTimeout(this.setTimeout);
      this.setTimeout = setTimeout(() => {
        this.displayCallback = false;
      }, 3000);
    },
  },
};
</script>
<style lang="sass" scoped>
.quiz
  display: flex
  justify-content: center
  align-items: center
  flex-direction: column
  padding-bottom: 1em
  padding-top: 2em
  +forTablet
    padding-top: 1em
  +forDesktop
    min-height: 70vh
  +element('container')
    position: relative
    overflow-y: auto
    box-shadow: 5px 5px 10px rgba(0,0,0,.5)
    border-radius: 30px
    background-color: #fff
    width: 100%
    +forTablet
      width: 98%
    +forDesktop
      margin-top: unset
      width: 850px
      height: 600px
  +element('callback')
    width: 50px
    border-top-right-radius: 30px
    border-bottom-right-radius: 30px
    margin-left: -30px
    padding-left: 30px
    background-color: rgba(244,232,52,.7)
    font-family: comfortaabold
    display: flex
    align-items: center
    color: #ff1800
    font-size: 0
    -webkit-transition: all 1s
    transition: all 1s
    text-align: center
    box-shadow: 5px 5px 10px rgba(0,0,0,.5)
    +modifier('display')
      width: 80px
      font-size: 22px
      +forTablet
        width: 200px
        font-size: 26px
      +forDesktop
        width: 250px
        font-size: 32px
  +element('callback-text')
    writing-mode: vertical-lr
    margin-left: .5em
    +forTablet
      width: 100%
      writing-mode: unset
      margin-left: unset
  +element('progress')
    text-align: center
    font-weight: 600
    width: 100%
    height: 100%
    display: flex
    justify-content: center
    height: 35px
    margin-bottom: 1em
    +forTablet
      margin-bottom: unset
    +forDesktop
      margin-bottom: 1em
  +element('progress-item')
    background-color: rgba($color: #add8e6, $alpha: 0.6)
    display: inline-block
    position: relative
    margin-right: 0.5rem
    border-radius: 50%
    color: #ff6d00
    height: 1.8em
    line-height: 1.8em
    text-align: center
    width: 1.8em
    z-index: 20
    font-size: 14px
    +forDesktop
      height: 2em
      width: 2em
      line-height: 2em
      font-size: 16px
    +modifier('active')
      background-color: #00b9ff
      color: #ffffff
.question
  +element('info')
    background-color: #00b9ff
    border-top-left-radius: 30px
    border-top-right-radius: 30px
    text-align: center
    padding: 1em
    color: #fff
    position: relative
    +forDesktop
      display: block
      align-items: unset
      justify-content: unset
  +element('info-time')
    +forDesktop
      position: absolute
      right: 1em
      top: calc( 50% - 8px )
  +element('info-text')
    font-size: 18px
    margin-bottom: .5em
    +forTablet
      margin-bottom: 0
  +element('text')
    font-size: 14px
    font-weight: 600
    text-align: center
    margin-bottom: 1em
  +element('example')
    font-size: 16px
    font-weight: 400
    text-align: center
    margin-bottom: .5em
  +element('multiple-choices')
    +forTabletLandscape
      margin-top: 1em
      columns: 2
      -webkit-columns: 2
      -moz-columns: 2
    +forDesktop
      columns: unset
  +element('multiple-choices-item')
    font-size: 12px
    background-color: #ff6d00
    border-radius: 5px
    font-family: comfortaabold
    text-transform: uppercase
    display: flex
    justify-content: center
    align-items: center
    min-height: 4em
    margin-bottom: .5em
    margin-top: .5em
    +forTablet
      margin-bottom: .5em
      margin-top: 0
    +forDesktop
      margin-bottom: 1em
  +element('multiple-choices-item-button')
    min-height: 4em
    width: 100%
    height: 100%
    color: #fff
    &:focus
      background-color: rgb(40, 167, 69)
      outline: none
      box-shadow: none
    +modifier('active')
      background-color: rgb(40, 167, 69)
  +element('two-choices')
    margin-top: 2em
    columns: 2
    -webkit-columns: 2
    -moz-columns: 2
  +element('two-choices-item')
    font-size: 12px
    background-color: #ff6d00
    border-radius: 5px
    font-family: comfortaabold
    text-transform: uppercase
    display: flex
    justify-content: center
    align-items: center
    min-height: 4em
    margin-bottom: .5em
    margin-top: .5em
    +forTablet
      margin-bottom: .5em
      margin-top: 0
    +forDesktop
      margin-bottom: 1em
  +element('two-choices-item-button')
    min-height: 4em
    width: 100%
    height: 100%
    color: #fff
    &:focus
      background-color: rgb(40, 167, 69)
      outline: none
      box-shadow: none
    +modifier('active')
      background-color: rgb(40, 167, 69)
  +element('write-answer')
    margin-top: 2em
    margin-bottom: 2em
  +element('write-answer-input')
    width: 250px
    margin: auto
  +element('actions')
    display: flex
    justify-content: space-around
    margin-bottom: 1em
    +forDesktop
      position: absolute
      bottom: 1em
      left: 0
      right: 0
.btn-skip,
.btn-submit
  justify-self: center
  color: #ffffff
  padding: 5px
  font-family: 'comfortaabold'
  font-weight: bold
  font-size: 18px
  width: 130px
  height: 35px
.btn-submit
  background-color: #7ac35c
  &:disabled
    background-color: #a0a0a0 !important
  &:focus
    box-shadow: 0 0 0 3px rgba($color: #7ac35c, $alpha: 0.5)
.btn-skip
  background-color: #ff6d00
  &:focus
    box-shadow: 0 0 0 3px rgba($color: #ff6d00, $alpha: 0.5)
.btn-exit
  position: fixed
  bottom: 0
  left: 0
  color: #ffffff
  background-color: #ff1800
  border-bottom-left-radius: 0
  border-top-left-radius: 0
  font-family: 'comfortaabold'
  font-weight: bold
  font-size: 18px
  display: flex
  align-items: center
  img
    margin-right: 10px
    width: 18px
  &:hover
    transform: none
  &:focus
    box-shadow: 0 0 0 3px rgba($color: #ff1800, $alpha: 0.5)
</style>
